import React, { useState, useEffect } from "react";
import { getTasks, saveTasks } from "./localStorageUtils";
import { FaHourglassHalf, FaSkull } from "react-icons/fa";
import { getNextSaturday } from "./utils";

const TaskList = ({ tasks, setTasks, updatePoints, completeReset }) => {
  const [newTask, setNewTask] = useState("");
  const [customDeadline, setCustomDeadline] = useState("");

  // Fetch tasks from server on mount
  useEffect(() => {
    const fetchServerTasks = async () => {
      try {
        const serverTasks = await getTasks();
        setTasks(serverTasks);
      } catch (error) {
        console.error("Failed to fetch tasks from server:", error);
      }
    };
    fetchServerTasks();
  }, [setTasks]);

  // Check deadlines every 10 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      const now = new Date();
      let changed = false;

      const updated = tasks.map((t) => {
        if (!t.completed && now >= new Date(t.deadline)) {
          if (!t.failed) changed = true;
          return { ...t, failed: true };
        }
        return t;
      });

      if (changed) {
        setTasks(updated);
        try {
          await saveTasks(updated);
        } catch (err) {
          console.error("Error saving tasks (deadline check):", err);
        }
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [tasks, setTasks]);

  // Add a new task
  const addTask = async (e) => {
    e.preventDefault();
    if (!newTask.trim()) return;

    const now = new Date();
    const defaultDeadline = getNextSaturday();
    let deadline = defaultDeadline;
    if (customDeadline) {
      const customDate = new Date(customDeadline);
      if (customDate >= now && customDate <= defaultDeadline) {
        deadline = customDate;
      }
    }

    const newEntry = {
      id: Date.now(),
      name: newTask,
      completed: false,
      deadline,
      failed: false,
      subtasks: [],
    };

    const updated = [newEntry, ...tasks];
    setTasks(updated);
    try {
      await saveTasks(updated);
    } catch (err) {
      console.error("Error saving tasks (addTask):", err);
    }

    setNewTask("");
    setCustomDeadline("");
  };

  // Toggle completion
  const toggleTaskCompletion = async (taskId) => {
    let changed = false;
    const updated = tasks.map((task) => {
      if (task.id === taskId) {
        changed = true;
        const completed = !task.completed;
        if (completed) {
          updatePoints(10);
        } else {
          updatePoints(-10);
        }
        return { ...task, completed };
      }
      return task;
    });

    if (changed) {
      setTasks(updated);
      try {
        await saveTasks(updated);
      } catch (err) {
        console.error("Error saving tasks (toggleTaskCompletion):", err);
      }
    }
  };

  // Delete task
  const deleteTask = async (id) => {
    const updated = tasks.filter((t) => t.id !== id);
    setTasks(updated);
    try {
      await saveTasks(updated);
    } catch (err) {
      console.error("Error saving tasks (deleteTask):", err);
    }
  };

  // Days left helper
  const daysLeft = (deadline) => {
    const now = new Date();
    const diff = new Date(deadline) - now;
    return Math.max(Math.ceil(diff / (1000 * 60 * 60 * 24)), 0);
  };

  return (
    <div className="p-4 bg-gradient-to-r from-[#00e5ff] via-[#86cecb] to-[#137a7f] rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-4 text-white">Today's Tasks</h2>

      <form className="mb-4 flex flex-col sm:flex-row items-center" onSubmit={addTask}>
        <input
          type="text"
          placeholder="Add a new task"
          className="p-2 border rounded flex-grow bg-[#373b3e] text-white mb-2 sm:mb-0"
          value={newTask}
          onChange={(e) => setNewTask(e.target.value)}
        />
        <input
          type="date"
          className="p-2 border rounded bg-[#373b3e] text-white ml-0 sm:ml-2 mb-2 sm:mb-0"
          value={customDeadline}
          onChange={(e) => setCustomDeadline(e.target.value)}
          min={new Date().toISOString().split("T")[0]}
          max={getNextSaturday().toISOString().split("T")[0]}
        />
        <button
          type="submit"
          className="ml-0 sm:ml-2 px-4 py-2 bg-[#e12885] text-white rounded hover:bg-pink-600"
        >
          Add Task
        </button>
      </form>

      {tasks.map((task) => (
        <div
          key={task.id}
          className={`p-4 my-4 rounded-lg shadow-lg transform transition-all duration-300 ${
            task.completed
              ? "bg-gradient-to-r from-[#e12885] to-[#00e5ff] text-white"
              : "bg-[#00e5ff] text-gray-800"
          }`}
        >
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">{task.name}</h2>
            <div className="flex items-center">
              <FaHourglassHalf className="mr-1" />
              <span>{daysLeft(task.deadline)} day(s) left</span>
              {task.failed && <FaSkull className="ml-2 text-red-700" />}
            </div>
          </div>
          <div className="flex justify-between items-center mt-2">
            <button
              className={`px-4 py-2 text-sm font-bold rounded ${
                task.completed
                  ? "bg-white text-[#e12885] hover:bg-gray-200"
                  : "bg-white text-[#137a7f] hover:bg-gray-100"
              }`}
              onClick={() => toggleTaskCompletion(task.id)}
            >
              {task.completed ? "Completed" : "Mark as Complete"}
            </button>
            <button
              className="px-4 py-2 text-sm font-bold bg-red-500 text-white rounded hover:bg-red-600"
              onClick={() => deleteTask(task.id)}
            >
              Delete
            </button>
          </div>
        </div>
      ))}

      <button
        className="mt-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
        onClick={completeReset}
      >
        Complete Reset
      </button>
    </div>
  );
};

export default TaskList;
